import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { generateContentTemplate } from '../../../store/request';
import type { GeneratedContentTemplate, TemplateChannel } from '../../../types/template';
import { scriptChannelMeta, type ScriptChannel } from './scriptData';

type ScriptGenerateModalProps = {
  open: boolean;
  onClose: () => void;
  /** 生成结果交给编辑弹窗确认后再保存。 */
  onGenerated: (result: { channel: ScriptChannel; name: string; subject?: string; content: string }) => void;
};

function toApiChannel(channel: ScriptChannel): TemplateChannel {
  if (channel === 'email') return 'EMAIL';
  return 'DM';
}

function toScriptChannel(channel?: string): ScriptChannel | null {
  const value = channel?.trim().toUpperCase();
  if (value === 'EMAIL') return 'email';
  if (value === 'DM') return 'dm';
  return null;
}

function generatedBody(content: GeneratedContentTemplate | string | null | undefined) {
  if (typeof content === 'string') return content.trim();
  return content?.body?.trim() ?? '';
}

export function ScriptGenerateModal({ open, onClose, onGenerated }: ScriptGenerateModalProps) {
  const { t } = useTranslation();
  const [channel, setChannel] = useState<ScriptChannel>('dm');
  const [nameHint, setNameHint] = useState('');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const channelOptions = useMemo(
    () =>
      (Object.keys(scriptChannelMeta) as ScriptChannel[]).map((value) => ({
        value,
        label: t(`script.channel_${value}`)
      })),
    [t]
  );

  useEffect(() => {
    if (open) {
      setChannel('dm');
      setNameHint('');
      setPrompt('');
      setGenerating(false);
    }
  }, [open]);

  const submit = async () => {
    const nextPrompt = prompt.trim();
    const nextNameHint = nameHint.trim();
    if (!nextPrompt) {
      message.warning(t('script.points_required'));
      return;
    }
    if (generating) return;
    setGenerating(true);
    try {
      const res = await generateContentTemplate({
        channel: toApiChannel(channel),
        prompt: nextPrompt.slice(0, 2000),
        ...(nextNameHint ? { nameHint: nextNameHint.slice(0, 255) } : {})
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('script.generate_failed'));
        return;
      }
      const payload = res.content;
      const body = generatedBody(payload);
      if (!body) {
        message.error(res.message || t('script.generate_failed'));
        return;
      }
      const template = typeof payload === 'string' ? undefined : payload;
      const point = nextPrompt.split(/[，,、;；\n]/)[0].trim().slice(0, 16);
      const subject = template?.subject?.trim();
      message.success(res.message || t('script.generated'));
      onGenerated({
        channel: toScriptChannel(template?.channel) ?? channel,
        name: template?.name?.trim() || nextNameHint || t('script.generated_name', { point }),
        ...(subject ? { subject } : {}),
        content: body
      });
    } catch {
      message.error(t('script.generate_failed'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal
      title={t('script.generate_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={480}
      closeIcon={
        <span className="cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700">{t('common.close')}</span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose} disabled={generating}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" className="wb-btn-primary rounded-lg" loading={generating} onClick={submit}>
            {t('script.generate')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-1">
        <div>
          <div className="mb-1.5 text-sm text-slate-500">{t('script.channel')}</div>
          <Select className="w-full" value={channel} onChange={setChannel} options={channelOptions} />
        </div>
        <div>
          <div className="mb-1.5 text-sm text-slate-500">{t('script.name_hint')}</div>
          <Input
            value={nameHint}
            maxLength={255}
            placeholder={t('script.name_hint_placeholder')}
            onChange={(e) => setNameHint(e.target.value)}
          />
        </div>
        <div>
          <div className="mb-1.5 text-sm text-slate-500">{t('script.selling_points')}</div>
          <Input.TextArea
            value={prompt}
            maxLength={2000}
            autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder={t('script.points_placeholder')}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
